import { getTheme, type Theme } from '../config/themes'
import { deepMergeThemeData } from './themeMerge'

const THEMES_BASE_PATH = '/themes'

// Cache for loaded themes
const themeCache = new Map<string, Theme>()
const loadingThemes = new Set<string>()

// Clear theme cache
export function clearThemeCache(): void {
  themeCache.clear()
  loadingThemes.clear()
}

function hasThemePalette(themeData: Record<string, unknown>): boolean {
  return Boolean(themeData.colors || themeData.modes)
}

async function fetchThemeJson(themeName: string): Promise<Record<string, unknown> | null> {
  const accountThemePath = `${THEMES_BASE_PATH}/${themeName}/css/custom.json`
  try {
    // Avoid stale custom.json (e.g. missing hideThemeToggle / forceColorMode).
    const accountResponse = await fetch(accountThemePath, { cache: 'no-store' })
    if (accountResponse.ok) {
      return (await accountResponse.json()) as Record<string, unknown>
    }
  } catch {
    // fall through
  }

  try {
    const response = await fetch(`${THEMES_BASE_PATH}/${themeName}.json`, { cache: 'no-store' })
    if (!response.ok) {
      return null
    }
    return (await response.json()) as Record<string, unknown>
  } catch {
    return null
  }
}

async function resolveThemeData(
  themeName: string,
  seen = new Set<string>(),
): Promise<Record<string, unknown> | null> {
  if (seen.has(themeName)) {
    throw new Error(`Circular theme extends detected: ${themeName}`)
  }
  seen.add(themeName)

  const raw = await fetchThemeJson(themeName)
  if (!raw) return null

  const extendsName =
    typeof raw.extends === 'string' && raw.extends.trim() ? raw.extends.trim() : ''

  if (!extendsName) {
    return { ...raw, name: themeName }
  }

  const parent = await resolveThemeData(extendsName, seen)
  if (!parent) {
    throw new Error(`Parent theme not found: ${extendsName}`)
  }

  const merged = deepMergeThemeData(parent, { ...raw, name: themeName })
  delete merged.extends
  return merged
}

function validateThemeData(themeData: Record<string, unknown>, source: string): void {
  if (!themeData.name || !hasThemePalette(themeData) || !themeData.typography) {
    throw new Error(`Invalid theme structure: ${source}`)
  }
}

export async function loadThemeFromFile(themeName: string): Promise<Theme | null> {
  // Check cache first
  const cachedTheme = themeCache.get(themeName)
  if (cachedTheme) {
    return cachedTheme
  }
  
  // If already loading this theme, wait for it
  if (loadingThemes.has(themeName)) {
    while (loadingThemes.has(themeName)) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
    // Check cache again after waiting
    const cachedThemeAfterWait = themeCache.get(themeName)
    if (cachedThemeAfterWait) {
      return cachedThemeAfterWait
    }
  }
  
  loadingThemes.add(themeName)
  
  try {
    const themeData = await resolveThemeData(themeName)
    if (!themeData) {
      loadingThemes.delete(themeName)
      return null
    }

    validateThemeData(themeData, `${THEMES_BASE_PATH}/${themeName}/css/custom.json`)
    themeData.name = themeName
    const theme = themeData as unknown as Theme
    themeCache.set(themeName, theme)
    loadingThemes.delete(themeName)
    return theme
  } catch (error) {
    console.error(`Error loading theme ${themeName}:`, error)
    loadingThemes.delete(themeName)
    return null
  }
}

export function resolveThemeName(
  urlTheme: string | null | undefined
): string {
  if (!urlTheme || urlTheme.trim() === '' || urlTheme.trim().toLowerCase() === 'default') {
    return 'default'
  }
  
  let cleaned = urlTheme.trim()
  
  const queryIndex = cleaned.indexOf('?')
  if (queryIndex !== -1) {
    cleaned = cleaned.substring(0, queryIndex)
  }
  
  const pathIndex = cleaned.indexOf('/')
  if (pathIndex !== -1) {
    cleaned = cleaned.substring(0, pathIndex)
  }
  
  const hashIndex = cleaned.indexOf('#')
  if (hashIndex !== -1) {
    cleaned = cleaned.substring(0, hashIndex)
  }
  
  return cleaned || 'default'
}

export async function loadThemeWithFallback(themeName: string): Promise<Theme> {
  const loadedTheme = await loadThemeFromFile(themeName)
  if (loadedTheme) return loadedTheme
  return getTheme('default')
}
